// Real-Time State Management Store

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  getWebSocketClient,
  useWebSocket,
  type MarketDataUpdate,
  type PositionUpdate,
  type SignalUpdate,
  type TradeUpdate,
  type PortfolioUpdate,
  type RiskAlert,
  type SystemStatus,
  type ConnectionState,
} from '../services/websocket';
import { usePortfolioStore, usePositionList } from './portfolioStore';
import { useTradingStore } from './tradingStore';
import type { Position } from '../types/api';

// Real-time state interface
// Note: Positions are managed by portfolioStore as the single source of truth
// RealtimeStore updates portfolioStore when receiving position updates via WebSocket
interface RealtimeState {
  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;
  lastHeartbeat: string | null;
  
  // Message rate tracking for adaptive polling
  messageCount: number;
  
  // Market data
  marketData: Map<string, MarketDataUpdate>;
  subscribedSymbols: string[];
  
  // Signals
  signals: SignalUpdate[];
  unreadSignals: number;
  
  // Trades
  recentTrades: TradeUpdate[];
  
  // Portfolio summary (real-time updates)
  portfolio: PortfolioUpdate | null;
  
  // Risk alerts
  riskAlerts: RiskAlert[];
  unreadAlerts: number;
  
  // System status
  systemStatus: SystemStatus | null;
  
  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  subscribeToSymbols: (symbols: string[]) => void;
  unsubscribeFromSymbols: (symbols: string[]) => void;
  markSignalsRead: () => void;
  markAlertsRead: () => void;
  clearTrades: () => void;
  clearSignals: () => void;
  clearAlerts: () => void;
  
  // Internal actions
  _setConnectionState: (state: ConnectionState) => void;
  _incrementMessageCount: () => void;
  _updateMarketData: (data: MarketDataUpdate) => void;
  _updatePosition: (data: PositionUpdate) => void;
  _handlePositionClose: (data: PositionUpdate) => void;
  _addSignal: (signal: SignalUpdate) => void;
  _addTrade: (trade: TradeUpdate) => void;
  _updatePortfolio: (portfolio: PortfolioUpdate) => void;
  _addRiskAlert: (alert: RiskAlert) => void;
  _setSystemStatus: (status: SystemStatus) => void;
  _setHeartbeat: (timestamp: string) => void;
}

// Create the store
export const useRealtimeStore = create<RealtimeState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    connectionState: 'disconnected',
    isConnected: false,
    lastHeartbeat: null,
    messageCount: 0,
    marketData: new Map(),
    subscribedSymbols: [],
    signals: [],
    unreadSignals: 0,
    recentTrades: [],
    portfolio: null,
    riskAlerts: [],
    unreadAlerts: 0,
    systemStatus: null,
    
    // Connect to WebSocket
    connect: async () => {
      const client = getWebSocketClient();
      
      // Set up event handlers
      client.onConnectionChange((state) => {
        set({
          connectionState: state,
          isConnected: state === 'connected',
        });
      });
      
      client.onMessage<MarketDataUpdate>('market_data', (message) => {
        get()._incrementMessageCount();
        get()._updateMarketData(message.data);
      });
      
      client.onMessage<PositionUpdate>('position:open', (message) => {
        get()._incrementMessageCount();
        get()._updatePosition(message.data);
      });

      client.onMessage<PositionUpdate>('position:close', (message) => {
        get()._incrementMessageCount();
        get()._handlePositionClose(message.data);
      });
      
      client.onMessage<SignalUpdate>('signal:new', (message) => {
        get()._incrementMessageCount();
        get()._addSignal(message.data);
      });
      
      client.onMessage<TradeUpdate>('activity:trade', (message) => {
        get()._incrementMessageCount();
        get()._addTrade(message.data);
      });
      
      client.onMessage<PortfolioUpdate>('portfolio:update', (message) => {
        get()._incrementMessageCount();
        get()._updatePortfolio(message.data);
      });
      
      client.onMessage<RiskAlert>('activity:risk', (message) => {
        get()._incrementMessageCount();
        get()._addRiskAlert(message.data);
      });
      
      client.onMessage<SystemStatus>('system:status', (message) => {
        get()._incrementMessageCount();
        get()._setSystemStatus(message.data);
      });
      
      client.onMessage('heartbeat', (message) => {
        get()._incrementMessageCount();
        get()._setHeartbeat(message.timestamp);
      });
      
      // Connect
      await client.connect();
    },
    
    // Disconnect from WebSocket
    disconnect: () => {
      const client = getWebSocketClient();
      client.disconnect();
      set({
        connectionState: 'disconnected',
        isConnected: false,
        marketData: new Map(),
      });
    },
    
    // Subscribe to symbols
    subscribeToSymbols: (symbols: string[]) => {
      const client = getWebSocketClient();
      const currentSymbols = get().subscribedSymbols;
      const newSymbols = symbols.filter(s => !currentSymbols.includes(s));
      
      if (newSymbols.length > 0) {
        client.subscribe({
          channels: ['market_data'],
          symbols: newSymbols,
        });
        
        set({
          subscribedSymbols: [...currentSymbols, ...newSymbols],
        });
      }
    },
    
    // Unsubscribe from symbols
    unsubscribeFromSymbols: (symbols: string[]) => {
      const client = getWebSocketClient();
      const currentSymbols = get().subscribedSymbols;
      const remainingSymbols = currentSymbols.filter(s => !symbols.includes(s));
      
      // Note: WebSocket doesn't support unsubscribing from specific symbols,
      // so we resubscribe with remaining symbols
      if (remainingSymbols.length !== currentSymbols.length) {
        client.unsubscribe(['market_data']);
        if (remainingSymbols.length > 0) {
          client.subscribe({
            channels: ['market_data'],
            symbols: remainingSymbols,
          });
        }
        
        set({ subscribedSymbols: remainingSymbols });
      }
    },
    
    // Mark signals as read
    markSignalsRead: () => set({ unreadSignals: 0 }),
    
    // Mark alerts as read
    markAlertsRead: () => set({ unreadAlerts: 0 }),
    
    // Clear trades
    clearTrades: () => set({ recentTrades: [] }),
    
    // Clear signals
    clearSignals: () => set({ signals: [], unreadSignals: 0 }),
    
    // Clear alerts
    clearAlerts: () => set({ riskAlerts: [], unreadAlerts: 0 }),
    
    // Internal actions
    _setConnectionState: (state) => set({ connectionState: state, isConnected: state === 'connected' }),
    
    _incrementMessageCount: () => {
      set((state) => ({ messageCount: state.messageCount + 1 }));
    },
    
    _updateMarketData: (data) => {
      set((state) => {
        const next = new Map(state.marketData);
        next.set(data.symbol, data);
        return { marketData: next };
      });
    },
    
    _updatePosition: (data) => {
      // Update portfolioStore as the single source of truth for positions
      // Convert PositionUpdate to Position format and update the store
      const { updatePosition } = usePortfolioStore.getState();
      if (data.size === 0) {
        // Position closed - delegate to _handlePositionClose for full handling
        get()._handlePositionClose(data);
      } else {
        // Update or add position
        updatePosition(data.symbol, {
          symbol: data.symbol,
          side: data.side,
          size: data.size,
          entryPrice: data.entryPrice,
          currentPrice: data.currentPrice,
          unrealizedPnL: data.unrealizedPnl,
          unrealizedPnLPercent: data.unrealizedPnlPercent,
        });
      }
    },

    _handlePositionClose: (data) => {
      // Remove position from portfolioStore (open positions)
      const { setPositions } = usePortfolioStore.getState();
      const currentPositions = usePortfolioStore.getState().positions;
      // BUG-FIX-25: match by symbol+side (parallel to backend BUG-FIX-23).
      // Symbol-only match would remove BOTH LONG and SHORT on same symbol.
      const closingPos = currentPositions.find(
        p => p.symbol === data.symbol && (!data.side || p.side === data.side)
      );
      setPositions(currentPositions.filter(
        p => !(p.symbol === data.symbol && (!data.side || p.side === data.side))
      ));

      // Build a closed Position object and add to tradingStore.closedPositions
      const { addClosedPosition } = useTradingStore.getState();
      const closedPosition: Position = {
        id: data.positionId || closingPos?.id || `${data.symbol}-closed-${Date.now()}`,
        symbol: data.symbol,
        side: data.side,
        size: closingPos?.size ?? data.size,
        entryPrice: data.entryPrice || closingPos?.entryPrice || 0,
        currentPrice: data.currentPrice,
        marketValue: 0,
        unrealizedPnL: data.unrealizedPnl,
        unrealizedPnLPercent: data.unrealizedPnlPercent,
        // BUG-FIX-17: Use realizedPnl (not unrealizedPnl) for closed position P&L.
        // At close time, the realized P&L is the final P&L, not the unrealized value.
        realizedPnL: data.realizedPnl ?? data.unrealizedPnl,
        openedAt: closingPos?.openedAt || new Date().toISOString(),
        status: 'closed',
        closedAt: new Date().toISOString(),
      };
      addClosedPosition(closedPosition);

      // Dispatch event so useWebSocketInvalidationListener can invalidate
      // the React Query cache for closed positions
      window.dispatchEvent(new CustomEvent('ws:position:update'));
    },
    
    _addSignal: (signal) => {
      set((state) => ({
        signals: [signal, ...state.signals].slice(0, 100),
        unreadSignals: state.unreadSignals + 1,
      }));
    },
    
    _addTrade: (trade) => {
      set((state) => ({
        recentTrades: [trade, ...state.recentTrades].slice(0, 100),
      }));
    },
    
    _updatePortfolio: (portfolio) => set({ portfolio }),
    
    _addRiskAlert: (alert) => {
      set((state) => ({
        riskAlerts: [alert, ...state.riskAlerts].slice(0, 50),
        unreadAlerts: state.unreadAlerts + 1,
      }));
    },
    
    _setSystemStatus: (status) => set({ systemStatus: status }),
    
    _setHeartbeat: (timestamp) => set({ lastHeartbeat: timestamp }),
  }))
);

// Selector hooks for optimized re-renders
export const useConnectionState = () => useRealtimeStore((state) => state.connectionState);
export const useIsConnected = () => useRealtimeStore((state) => state.isConnected);
export const useMarketData = (symbol?: string) => 
  useRealtimeStore((state) => symbol ? state.marketData.get(symbol) : state.marketData);
// Note: usePositions is now imported from portfolioStore - the single source of truth
export const useSignals = () => useRealtimeStore((state) => state.signals);
export const useUnreadSignals = () => useRealtimeStore((state) => state.unreadSignals);
export const useRecentTrades = () => useRealtimeStore((state) => state.recentTrades);
// Renamed from usePortfolio to avoid collision with portfolioStore's usePortfolio hook.
// Consumers wanting the real-time PortfolioUpdate shape should use this.
// Consumers wanting the full Portfolio shape should use usePortfolio from portfolioStore.
export const useRealtimePortfolio = () => useRealtimeStore((state) => state.portfolio);
export const useRiskAlerts = () => useRealtimeStore((state) => state.riskAlerts);
export const useUnreadAlerts = () => useRealtimeStore((state) => state.unreadAlerts);
export const useSystemStatus = () => useRealtimeStore((state) => state.systemStatus);

// Derived data selectors - positions now come from portfolioStore
// Re-export position hooks from portfolioStore for convenience
export { usePositions, usePositionList, useActivePositions } from './portfolioStore';
export const useTotalPnL = () => usePortfolioStore((state) => state.getTotalPnL());

export const useCriticalAlerts = () => 
  useRealtimeStore((state) => 
    state.riskAlerts.filter((alert) => alert.severity === 'critical')
  );

// Real-time data provider component
import { useEffect, useRef, useState } from 'react';

interface RealtimeProviderProps {
  children: React.ReactNode;
  autoConnect?: boolean;
  symbols?: string[];
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({
  children,
  autoConnect = true,
  symbols = [],
}) => {
  const { connect, disconnect, subscribeToSymbols, connectionState } = useRealtimeStore();
  const [error, setError] = useState<Error | null>(null);
  // Guard against React 18 Strict Mode running effects twice — only connect once.
  const didConnect = useRef(false);
  
  useEffect(() => {
    if (autoConnect && !didConnect.current) {
      didConnect.current = true;
      connect().catch((err) => {
        console.error('Failed to connect to WebSocket:', err);
        setError(err);
      });
    }
    
    return () => {
      disconnect();
      didConnect.current = false;
    };
  }, [autoConnect, connect, disconnect]);
  
  useEffect(() => {
    if (connectionState === 'connected' && symbols.length > 0) {
      subscribeToSymbols(symbols);
    }
  }, [connectionState, symbols, subscribeToSymbols]);
  
  if (error) {
    console.error('Real-time connection error:', error);
  }
  
  return <>{children}</>;
};

// Connection status indicator component
export const ConnectionStatus: React.FC = () => {
  const connectionState = useConnectionState();
  const lastHeartbeat = useRealtimeStore((state) => state.lastHeartbeat);
  
  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  const getStatusText = () => {
    switch (connectionState) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnecting':
        return 'Disconnecting...';
      case 'error':
        return 'Error';
      default:
        return 'Disconnected';
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${getStatusColor()} ${connectionState === 'connecting' ? 'animate-pulse' : ''}`} />
      <span className="text-xs text-gray-500">{getStatusText()}</span>
    </div>
  );
};

// Real-time price display component
interface RealtimePriceProps {
  symbol: string;
  showChange?: boolean;
}

export const RealtimePrice: React.FC<RealtimePriceProps> = ({ symbol, showChange = true }) => {
  const marketData = useMarketData(symbol) as MarketDataUpdate | undefined;
  
  if (!marketData) {
    return <span className="text-gray-400">--</span>;
  }
  
  const changeColor = marketData.change24h >= 0 ? 'text-green-500' : 'text-red-500';
  const changeSymbol = marketData.change24h >= 0 ? '+' : '';
  
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono font-medium">${marketData.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      {showChange && (
        <span className={`text-sm ${changeColor}`}>
          {changeSymbol}{marketData.change24h.toFixed(2)}%
        </span>
      )}
    </div>
  );
};

// Real-time position list component
export const RealtimePositionList: React.FC = () => {
  const positions = usePositionList();
  
  if (positions.length === 0) {
    return <div className="text-gray-500 text-sm">No open positions</div>;
  }
  
  return (
    <div className="space-y-2">
      {positions.map((position) => (
        <div key={position.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
          <div>
            <span className="font-medium">{position.symbol}</span>
            <span className={`ml-2 text-xs px-1 rounded ${position.side === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {position.side.toUpperCase()}
            </span>
          </div>
          <div className="text-right">
            <div className="font-mono">{position.size}</div>
            <div className={`text-sm ${(position.unrealizedPnL ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {(position.unrealizedPnL ?? 0) >= 0 ? '+' : ''}{(position.unrealizedPnL ?? 0).toFixed(2)} ({(position.unrealizedPnLPercent ?? 0).toFixed(2)}%)
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Real-time alerts component
export const RealtimeAlerts: React.FC<{ maxItems?: number }> = ({ maxItems = 5 }) => {
  const alerts = useRiskAlerts();
  const unreadAlerts = useUnreadAlerts();
  const markAlertsRead = useRealtimeStore((state) => state.markAlertsRead);
  
  const displayAlerts = alerts.slice(0, maxItems);
  
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 border-red-500 text-red-700';
      case 'warning':
        return 'bg-yellow-100 border-yellow-500 text-yellow-700';
      default:
        return 'bg-blue-100 border-blue-500 text-blue-700';
    }
  };
  
  return (
    <div className="space-y-2">
      {unreadAlerts > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{unreadAlerts} new alerts</span>
          <button onClick={markAlertsRead} className="text-xs text-blue-600 hover:underline">
            Mark all read
          </button>
        </div>
      )}
      {displayAlerts.map((alert) => (
        <div key={alert.alertId} className={`p-2 border-l-4 rounded ${getSeverityColor(alert.severity)}`}>
          <div className="font-medium">{alert.type.replace('_', ' ').toUpperCase()}</div>
          <div className="text-sm">{alert.message}</div>
          <div className="text-xs opacity-75">{new Date(alert.timestamp).toLocaleTimeString()}</div>
        </div>
      ))}
      {alerts.length === 0 && (
        <div className="text-gray-500 text-sm">No alerts</div>
      )}
    </div>
  );
};